import { AlertSeverity } from '@mulesoft/monitoring-center-components/lib/components/Selector/SeveritySelector/model'
import { isAllFilter } from './AlertsListPageFilters/filters'
import { Alert, AlertResourceType, BasicAlert, isBasicAlert } from '../model'

export type AlertState = {
  alerts: Alert[]
  filtered: Alert[]
  loading: boolean
  filters: AlertsFilters & {
    search?: string
  }
  nextLink?: string
  filteredNextLink?: string
}

export type AdvancedAlert = {
  alertId: string,
  alertName: string,
  alertState: string,
  alertType: 'complex',
  dashboardId: number,
  dashboardSlug: string
  evalData: EvalData,
  evalDate: any,
  executionError: string,
  isLoading: boolean
  message: string,
  newStateDate: string,
  panelId: number,
  severity: AlertSeverity | null
}

export type EvalData = {
  evalMatches: EvalMatches[]
}

export type EvalMatches = {
  metric: string,
  tags: any,
  value: number
}

export type AlertsFilters = {
  state: string
  severity: string
  resource: string
  type?: string
}

export const isFilterSelected = (key: string, value: string) => {
  switch (key) {
    default:
    case 'state':
    case 'severity':
      return !isAllFilter(value)
  }
}

export const hasFiltersChanged = (f1: AlertsFilters & {search?: string}, f2: AlertsFilters & {search?: string}) => {
  return f1.state !== f2.state || f1.severity !== f2.severity || f1.search !== f2.search || f1.type !== f2.type
}

export const getAlertSeverityHierarchy = (severity: AlertSeverity | null) => {
  switch (severity) {
    case AlertSeverity.CRITICAL:
      return 0
    case AlertSeverity.WARNING:
      return 1
    case AlertSeverity.INFO:
      return 2
    default:
      return 3
  }
}

export const getSeverityLabelFromAlert = (alert: AdvancedAlert | BasicAlert): string => {
  return getSeverityLabel(alert.severity)
}

export const getSeverityLabel = (s: AlertSeverity | undefined | null): string => {
  return s ? `${s.charAt(0).toLocaleUpperCase()}${s.slice(1)}` : 'Not defined'
}

export const severitySort = (rowA: AdvancedAlert | BasicAlert, rowB: AdvancedAlert | BasicAlert) => {
  const rowASeverity = getAlertSeverityHierarchy(rowA.severity)
  const rowBSeverity = getAlertSeverityHierarchy(rowB.severity)
  return rowASeverity < rowBSeverity ? -1 : rowASeverity > rowBSeverity ? 1 : 0
}

export const alertNameSort = (rowA: AdvancedAlert | BasicAlert, rowB: AdvancedAlert | BasicAlert) => {
  const rowAName = rowA.alertName
  const rowBName = rowB.alertName
  return caseSensitiveSorter(rowAName, rowBName)
}

export const caseSensitiveSorter = (a: string, b: string): number => {
  if (a === b){
    return 0
  }

  if (a.charAt(0) === b.charAt(0)){
    return caseSensitiveSorter(a.slice(1), b.slice(1))
  }

  if (a.charAt(0).toLowerCase() === b.charAt(0).toLowerCase()){
    if (/^[a-z]/.test(a.charAt(0)) && /^[A-Z]/.test(b.charAt(0))){
      return -1;
    }
    if (/^[a-z]/.test(b.charAt(0)) && /^[A-Z]/.test(a.charAt(0))){
      return 1;
    }
  }

  return a.localeCompare(b)
}

export const getResourceTypeLabelFromAlert = (alert: AdvancedAlert | BasicAlert): string => {
  return isBasicAlert(alert) ? getResourceTypeLabel(alert.resource.type) : 'Custom dashboard panel'
}

export const getResourceTypeLabel = (resourceType: AlertResourceType): string => {
  return resourceType !== AlertResourceType.API ? `${resourceType.charAt(0).toUpperCase()}${resourceType.slice(1)}`.replace('_', ' ')
    : resourceType.toUpperCase()
}

export const resourceTypeSort = (rowA: AdvancedAlert | BasicAlert, rowB: AdvancedAlert | BasicAlert) => {
  const rowAResourceType = getResourceTypeLabelFromAlert(rowA)
  const rowBResourceType = getResourceTypeLabelFromAlert(rowB)
  return rowAResourceType.localeCompare(rowBResourceType)
}
